import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2, Circle, Sparkles, ChevronDown, ChevronUp,
  HelpCircle, MessageCircle, Mail, BookOpen, ExternalLink,
  Loader2, AlertCircle, PartyPopper, Share2, Copy, Gift, X,
} from "lucide-react";
import confetti from "canvas-confetti";
import { orgApi } from "../lib/organizerApi";

const CELEBRATED_KEY = "nailViet.onboarding_celebrated";

/** Frontend-only deterministic referral code (no backend storage yet). */
function buildReferralCode(orgId) {
  if (!orgId) return "NVC-GUEST";
  // 6-char base-36 fingerprint, padded
  const tag = (Number(orgId) + 100000).toString(36).toUpperCase().padStart(4, "0");
  return `NVC-${tag}`;
}

function fireConfetti() {
  // Two short bursts from corners → premium, not obnoxious
  const opts = { spread: 70, ticks: 120, gravity: 0.85, scalar: 0.9, startVelocity: 35 };
  confetti({ ...opts, particleCount: 80, angle: 60, origin: { x: 0, y: 0.85 } });
  confetti({ ...opts, particleCount: 80, angle: 120, origin: { x: 1, y: 0.85 } });
  setTimeout(() => {
    confetti({ ...opts, particleCount: 60, angle: 90, origin: { x: 0.5, y: 0.6 } });
  }, 220);
}

/**
 * Onboarding panel — Phase 6.2 + gamification.
 *
 * Shows: progress bar (0–100%), event launch checklist (from
 * /api/organizer/onboarding-status), three primary verification badges, and a
 * collapsible Help Center module. When the organizer hits 100% for the first
 * time, a confetti burst fires once and a Celebration card appears with a
 * Share-your-event CTA and a Refer-another-organizer CTA.
 */
export default function OrganizerOnboardingPanel({ events = [] }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [helpOpen, setHelpOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [confettiFired, setConfettiFired] = useState(false);
  const [showReferModal, setShowReferModal] = useState(false);
  const [shareToast, setShareToast] = useState("");
  const celebrateGuardRef = useRef(false);

  const load = async () => {
    setLoading(true); setError("");
    try {
      const { data } = await orgApi.get("/organizer/onboarding-status");
      setStatus(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Không thể tải tiến độ onboarding.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const isComplete = (status?.percent ?? 0) >= 100;
  const organizerId = status?.organizer?.id;
  const referralCode = useMemo(() => buildReferralCode(organizerId), [organizerId]);
  const celebrateKey = useMemo(
    () => (organizerId ? `${CELEBRATED_KEY}:${organizerId}` : null),
    [organizerId]
  );

  // Fire confetti exactly once per organizer when they first hit 100%.
  useEffect(() => {
    if (!isComplete || !celebrateKey) return;
    if (celebrateGuardRef.current) return;
    let alreadyCelebrated = false;
    try { alreadyCelebrated = !!localStorage.getItem(celebrateKey); } catch (_e) { /* ignore */ }
    celebrateGuardRef.current = true;
    if (!alreadyCelebrated) {
      try { localStorage.setItem(celebrateKey, new Date().toISOString()); } catch (_e) { /* ignore */ }
      setConfettiFired(true);
      // Defer slightly so DOM is painted first (smoother on mobile)
      setTimeout(() => { try { fireConfetti(); } catch (_e) { /* ignore */ } }, 150);
    }
  }, [isComplete, celebrateKey]);

  // Shareable event picked for the share-card: prefer the most recent active event.
  const shareEvent = useMemo(() => {
    if (!events || events.length === 0) return null;
    const live = events.find((e) => e.active && !e.archived);
    return live || events[0];
  }, [events]);

  const shareUrl = useMemo(() => {
    if (!shareEvent) return "";
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    return `${origin}/events/${shareEvent.slug}`;
  }, [shareEvent]);

  const flashToast = (msg) => {
    setShareToast(msg);
    setTimeout(() => setShareToast(""), 2200);
  };

  const handleShareEvent = async () => {
    if (!shareUrl) return;
    const shareText = shareEvent
      ? `Tôi vừa ra mắt sự kiện "${shareEvent.name}" trên Nail Viet Club — đăng ký ngay!`
      : "Khám phá sự kiện trên Nail Viet Club!";
    try {
      if (typeof navigator !== "undefined" && navigator.share) {
        await navigator.share({ title: shareEvent?.name || "Nail Viet Club", text: shareText, url: shareUrl });
        return;
      }
    } catch (_e) { /* user cancelled — fall through to copy */ }
    try {
      await navigator.clipboard.writeText(shareUrl);
      flashToast("Đã sao chép liên kết sự kiện");
    } catch (_e) {
      flashToast("Không thể sao chép — hãy thử lại");
    }
  };

  const handleCopyReferral = async () => {
    try {
      await navigator.clipboard.writeText(referralCode);
      flashToast(`Đã sao chép mã: ${referralCode}`);
    } catch (_e) {
      flashToast("Không thể sao chép mã");
    }
  };

  const badges = useMemo(() => {
    if (!status) return [];
    const find = (k) => status.items?.find((i) => i.key === k)?.done || false;
    return [
      { key: "email_verified", label: "Email", done: find("email_verified") },
      { key: "phone_verified", label: "SĐT",   done: find("phone_verified") },
      { key: "first_event",    label: "Sự kiện", done: find("first_event") },
    ];
  }, [status]);

  if (loading) {
    return (
      <div data-testid="onboarding-loading" className="rounded-3xl bg-white border border-espresso/10 p-5 mb-6 flex items-center gap-3">
        <Loader2 className="w-4 h-4 animate-spin text-espresso-soft" />
        <span className="text-sm text-espresso-soft">Đang tải tiến trình thiết lập…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="onboarding-error" className="rounded-3xl bg-white border border-red-200 p-5 mb-6 flex items-center gap-3 text-red-800 text-sm">
        <AlertCircle className="w-4 h-4" />
        {error}
        <button onClick={load} className="ml-auto text-xs underline">Thử lại</button>
      </div>
    );
  }

  if (!status) return null;

  if (isComplete && collapsed) {
    return (
      <div data-testid="onboarding-complete-banner" className="rounded-3xl bg-[#E1EDE6] border border-[#2D4A3E]/15 p-4 mb-6 flex items-center gap-3">
        <CheckCircle2 className="w-5 h-5 text-[#2D4A3E]" />
        <div className="text-sm text-[#2D4A3E] font-medium flex-1">
          Bạn đã hoàn tất thiết lập tài khoản — chúc mừng!
        </div>
        <button
          onClick={() => setCollapsed(false)}
          data-testid="onboarding-show-checklist"
          className="text-xs uppercase tracking-[0.18em] text-[#2D4A3E] hover:underline"
        >
          Xem lại danh sách
        </button>
      </div>
    );
  }

  return (
    <section data-testid="organizer-onboarding-panel" className="rounded-3xl bg-white border border-espresso/10 p-5 sm:p-6 mb-6">
      <header className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-espresso-muted font-semibold flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> Khởi đầu của bạn
          </div>
          <h2 className="mt-1 text-xl sm:text-2xl font-serif text-espresso">
            {isComplete ? "Tài khoản đã sẵn sàng" : "Hoàn tất thiết lập tài khoản"}
          </h2>
          <p className="mt-1 text-sm text-espresso-soft">
            {isComplete
              ? "Cảm ơn bạn đã hoàn thành toàn bộ các bước thiết lập. Bạn có thể chỉnh sửa thông tin bất cứ lúc nào."
              : `Bạn đã hoàn thành ${status.completed}/${status.total} bước. Hoàn tất nốt để mở khóa toàn bộ tính năng.`}
          </p>
        </div>
        {isComplete && (
          <button
            onClick={() => setCollapsed(true)}
            data-testid="onboarding-collapse"
            className="text-xs uppercase tracking-[0.18em] text-espresso-muted hover:text-espresso"
          >
            Ẩn
          </button>
        )}
      </header>

      {/* Celebration (only when 100% & expanded) */}
      {isComplete && (
        <CelebrationCard
          confettiFired={confettiFired}
          referralCode={referralCode}
          shareEvent={shareEvent}
          shareUrl={shareUrl}
          onShare={handleShareEvent}
          onOpenRefer={() => setShowReferModal(true)}
        />
      )}

      {/* Progress bar */}
      <div className="mt-5">
        <div className="flex items-center justify-between text-xs font-medium">
          <span className="text-espresso-soft">Tiến độ</span>
          <span data-testid="onboarding-percent" className="text-espresso font-serif text-base">
            {status.percent}%
          </span>
        </div>
        <div className="mt-1.5 h-2 rounded-full bg-linen-100/60 overflow-hidden">
          <div
            data-testid="onboarding-progress-bar"
            className="h-full bg-espresso transition-all duration-500"
            style={{ width: `${status.percent}%` }}
          />
        </div>
      </div>

      {/* Verification badges */}
      <div className="mt-5 grid grid-cols-3 gap-2 sm:gap-3" data-testid="onboarding-badges">
        {badges.map((b) => (
          <VerificationBadge key={b.key} label={b.label} done={b.done} testId={`badge-${b.key}`} />
        ))}
      </div>

      {/* Checklist */}
      <ul className="mt-5 space-y-2" data-testid="onboarding-checklist">
        {status.items.map((it) => (
          <ChecklistRow key={it.key} item={it} />
        ))}
      </ul>

      {/* Quick links */}
      <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-2">
        <Link
          to="/organizer/events/new"
          data-testid="onboarding-create-event"
          className="inline-flex items-center justify-center gap-2 bg-espresso text-cream rounded-full px-4 py-2.5 text-xs uppercase tracking-[0.22em] font-semibold"
        >
          Tạo sự kiện đầu tiên
        </Link>
        <a
          href="/ai"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="onboarding-open-ai"
          className="inline-flex items-center justify-center gap-2 border border-espresso/15 text-espresso rounded-full px-4 py-2.5 text-xs uppercase tracking-[0.22em] font-semibold"
        >
          <MessageCircle className="w-3.5 h-3.5" /> Hỏi Cố Vấn AI
        </a>
        <button
          type="button"
          onClick={() => setHelpOpen((v) => !v)}
          data-testid="onboarding-toggle-help"
          className="inline-flex items-center justify-center gap-2 border border-espresso/15 text-espresso rounded-full px-4 py-2.5 text-xs uppercase tracking-[0.22em] font-semibold"
        >
          <HelpCircle className="w-3.5 h-3.5" />
          {helpOpen ? "Ẩn trợ giúp" : "Trợ giúp"}
        </button>
      </div>

      {helpOpen && <HelpCenter />}

      {showReferModal && (
        <ReferModal
          code={referralCode}
          onClose={() => setShowReferModal(false)}
          onCopy={handleCopyReferral}
        />
      )}

      {shareToast && (
        <div data-testid="onboarding-toast" className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-espresso text-cream rounded-full px-4 py-2 text-sm shadow-lg z-50">
          {shareToast}
        </div>
      )}
    </section>
  );
}

function CelebrationCard({ confettiFired, referralCode, shareEvent, shareUrl, onShare, onOpenRefer }) {
  return (
    <div
      data-testid="onboarding-celebration"
      data-confetti-fired={confettiFired ? "true" : "false"}
      className="mt-5 rounded-2xl border border-gold-500/30 bg-gradient-to-br from-[#FBEFD0] via-[#F8E6B5] to-[#FBEFD0] p-5 sm:p-6 relative overflow-hidden"
    >
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-2xl bg-espresso text-cream flex items-center justify-center flex-shrink-0">
          <PartyPopper className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#7A5C16] font-semibold">
            Chúc mừng!
          </div>
          <h3 className="mt-0.5 font-serif text-xl sm:text-2xl text-espresso">
            Tài khoản của bạn đã hoàn tất 100%
          </h3>
          <p className="mt-1 text-sm text-espresso-soft">
            Hãy chia sẻ sự kiện với cộng đồng và mời thêm các nhà tổ chức khác cùng tham gia Nail Viet Club.
          </p>
        </div>
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-3">
        {/* Share-your-event card */}
        <div data-testid="share-event-card" className="rounded-2xl bg-white/70 border border-gold-500/20 p-4 backdrop-blur-sm">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-semibold text-[#7A5C16]">
            <Share2 className="w-3 h-3" /> Chia sẻ sự kiện
          </div>
          {shareEvent ? (
            <>
              <div className="mt-1.5 text-sm font-semibold text-espresso truncate" data-testid="share-event-name">
                {shareEvent.name}
              </div>
              <div className="text-xs text-espresso-muted truncate">{shareUrl}</div>
              <button
                type="button"
                onClick={onShare}
                data-testid="share-event-copy"
                className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-espresso text-cream rounded-full px-4 py-2 text-xs uppercase tracking-[0.22em] font-semibold"
              >
                <Copy className="w-3.5 h-3.5" /> Sao chép & chia sẻ
              </button>
            </>
          ) : (
            <>
              <div className="mt-1.5 text-sm text-espresso-soft">
                Chưa có sự kiện. Tạo sự kiện đầu tiên để chia sẻ với khách của bạn.
              </div>
              <Link
                to="/organizer/events/new"
                data-testid="share-event-create"
                className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-espresso text-cream rounded-full px-4 py-2 text-xs uppercase tracking-[0.22em] font-semibold"
              >
                Tạo sự kiện
              </Link>
            </>
          )}
        </div>

        {/* Refer-another-organizer CTA */}
        <div data-testid="refer-organizer-card" className="rounded-2xl bg-white/70 border border-gold-500/20 p-4 backdrop-blur-sm">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-semibold text-[#7A5C16]">
            <Gift className="w-3 h-3" /> Mời nhà tổ chức khác
          </div>
          <div className="mt-1.5 text-sm font-semibold text-espresso">
            Cùng phát triển cộng đồng
          </div>
          <div className="text-xs text-espresso-muted">
            Mã giới thiệu của bạn:{" "}
            <span data-testid="refer-code-preview" className="font-mono font-semibold text-espresso">
              {referralCode}
            </span>
          </div>
          <button
            type="button"
            onClick={onOpenRefer}
            data-testid="refer-organizer-cta"
            className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-gold-500 text-charcoal rounded-full px-4 py-2 text-xs uppercase tracking-[0.22em] font-semibold hover:bg-gold-400"
          >
            <Gift className="w-3.5 h-3.5" /> Mời ngay
          </button>
        </div>
      </div>
    </div>
  );
}

function ReferModal({ code, onClose, onCopy }) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const signupLink = `${origin}/organizer/signup?ref=${encodeURIComponent(code)}`;
  return (
    <div
      data-testid="refer-modal"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-charcoal/60 backdrop-blur-sm px-4 py-6"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-white rounded-3xl p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-espresso-muted font-semibold">
              Mời nhà tổ chức
            </div>
            <h3 className="mt-0.5 font-serif text-xl text-espresso">Mã giới thiệu của bạn</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="refer-modal-close"
            aria-label="Đóng"
            className="text-espresso-muted hover:text-espresso"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mt-4 rounded-2xl bg-linen-100/50 border border-espresso/10 p-4 text-center">
          <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-espresso-muted">Mã</div>
          <div data-testid="refer-code" className="mt-1 font-mono text-2xl font-bold text-espresso tracking-wider">
            {code}
          </div>
        </div>

        <p className="mt-4 text-sm text-espresso-soft leading-relaxed">
          Chia sẻ liên kết bên dưới với chủ tiệm nail khác. Khi họ đăng ký bằng mã của bạn,
          cả hai sẽ được mở khóa quà tặng cộng đồng trong bản cập nhật sắp tới.
        </p>

        <div className="mt-3 rounded-xl border border-espresso/10 bg-linen-100/30 px-3 py-2 text-xs text-espresso-muted break-all" data-testid="refer-link">
          {signupLink}
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={onCopy}
            data-testid="refer-copy-code"
            className="inline-flex items-center justify-center gap-2 bg-espresso text-cream rounded-full px-4 py-2.5 text-xs uppercase tracking-[0.22em] font-semibold"
          >
            <Copy className="w-3.5 h-3.5" /> Sao chép mã
          </button>
          <button
            type="button"
            onClick={async () => {
              try { await navigator.clipboard.writeText(signupLink); } catch (_e) { /* ignore */ }
              onCopy();
            }}
            data-testid="refer-copy-link"
            className="inline-flex items-center justify-center gap-2 border border-espresso/20 text-espresso rounded-full px-4 py-2.5 text-xs uppercase tracking-[0.22em] font-semibold"
          >
            <Share2 className="w-3.5 h-3.5" /> Sao chép liên kết
          </button>
        </div>

        <p className="mt-3 text-[10px] uppercase tracking-[0.22em] text-espresso-muted text-center">
          Beta · Phần thưởng sẽ công bố sớm
        </p>
      </div>
    </div>
  );
}

function VerificationBadge({ label, done, testId }) {
  return (
    <div
      data-testid={testId}
      className={`rounded-2xl border px-3 py-3 flex items-center gap-2 transition-colors ${
        done
          ? "border-[#2D4A3E]/20 bg-[#E1EDE6]/70 text-[#2D4A3E]"
          : "border-espresso/10 bg-linen-100/40 text-espresso-muted"
      }`}
    >
      {done ? (
        <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
      ) : (
        <Circle className="w-4 h-4 flex-shrink-0" />
      )}
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.22em] font-semibold">
          {done ? "Đã xác minh" : "Chưa xác minh"}
        </div>
        <div className="text-sm font-semibold truncate">{label}</div>
      </div>
    </div>
  );
}

function ChecklistRow({ item }) {
  return (
    <li
      data-testid={`checklist-${item.key}`}
      className={`flex items-center gap-3 rounded-xl border px-3.5 py-2.5 ${
        item.done
          ? "border-[#2D4A3E]/15 bg-[#E1EDE6]/40"
          : "border-espresso/10 bg-linen-100/40"
      }`}
    >
      {item.done ? (
        <CheckCircle2 className="w-4 h-4 flex-shrink-0 text-[#2D4A3E]" />
      ) : (
        <Circle className="w-4 h-4 flex-shrink-0 text-espresso-muted" />
      )}
      <div className={`text-sm flex-1 ${item.done ? "text-[#2D4A3E] line-through decoration-1" : "text-espresso"}`}>
        {item.label_vi || item.label_en}
      </div>
      {item.done && (
        <span className="text-[10px] uppercase tracking-[0.18em] text-[#2D4A3E] font-semibold">
          Xong
        </span>
      )}
    </li>
  );
}

const FAQ_ITEMS = [
  {
    q: "Làm sao để tạo sự kiện đầu tiên?",
    a: "Bấm nút \"Tạo sự kiện mới\" ở đầu trang. Bạn có thể nhập tên, ngày, địa điểm, và xuất bản ngay sau khi hoàn tất.",
  },
  {
    q: "Mã kiosk và mã PIN dùng để làm gì?",
    a: "Mỗi sự kiện có một mã kiosk và mã PIN duy nhất. Nhân viên đăng nhập tại /checkin bằng cặp mã này để mở giao diện check-in cho đúng sự kiện của bạn.",
  },
  {
    q: "Tôi có thể thay đổi mật khẩu hoặc số điện thoại không?",
    a: "Hiện tại bạn có thể yêu cầu liên kết thiết lập lại qua email. Tính năng thay đổi trực tiếp trong dashboard sẽ ra mắt trong bản cập nhật tiếp theo.",
  },
  {
    q: "Tôi cần hỗ trợ thêm — tôi có thể liên hệ với ai?",
    a: "Bạn có thể trò chuyện với Cố Vấn AI bất cứ lúc nào, hoặc liên hệ đội ngũ Nail Viet Club qua trang Liên hệ.",
  },
];

function HelpCenter() {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <div className="mt-5 rounded-2xl border border-espresso/10 bg-linen-100/30 p-4 sm:p-5" data-testid="help-center">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-semibold text-espresso-soft mb-3">
        <BookOpen className="w-3 h-3" /> Trung tâm trợ giúp
      </div>

      <ul className="space-y-2">
        {FAQ_ITEMS.map((it, i) => {
          const open = openIdx === i;
          return (
            <li key={i} className="rounded-xl bg-white border border-espresso/10 overflow-hidden">
              <button
                type="button"
                onClick={() => setOpenIdx(open ? null : i)}
                data-testid={`faq-toggle-${i}`}
                className="w-full flex items-center justify-between gap-3 px-3.5 py-2.5 text-left"
              >
                <span className="text-sm font-medium text-espresso">{it.q}</span>
                {open ? <ChevronUp className="w-4 h-4 text-espresso-soft" /> : <ChevronDown className="w-4 h-4 text-espresso-soft" />}
              </button>
              {open && (
                <div data-testid={`faq-answer-${i}`} className="px-3.5 pb-3 text-sm text-espresso-soft leading-relaxed">
                  {it.a}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <div className="mt-4 grid sm:grid-cols-2 gap-2">
        <a
          href="/ai"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="help-open-ai"
          className="inline-flex items-center justify-center gap-2 bg-espresso text-cream rounded-full px-4 py-2.5 text-xs uppercase tracking-[0.22em] font-semibold"
        >
          <MessageCircle className="w-3.5 h-3.5" /> Mở Cố Vấn AI
          <ExternalLink className="w-3 h-3" />
        </a>
        <Link
          to="/contact"
          data-testid="help-open-contact"
          className="inline-flex items-center justify-center gap-2 border border-espresso/20 text-espresso rounded-full px-4 py-2.5 text-xs uppercase tracking-[0.22em] font-semibold"
        >
          <Mail className="w-3.5 h-3.5" /> Liên hệ hỗ trợ
        </Link>
      </div>
    </div>
  );
}
